/**
 * Textarea — Multi-line text input for longer-form content.
 *
 * Textarea Component Guidelines
 * - Use Textarea for multi-line values (descriptions, notes, code). For single-line
 *   input, use Input instead.
 * - Use `variant="short"` (default, 56px min-height) for brief inputs like comments;
 *   `variant="long"` (80px min-height) for content that typically spans multiple lines
 *   like descriptions or messages.
 * - Use `size="sm"` in compact areas like sidebars or popovers; `size="base"` for
 *   standard forms.
 * - Always wrap in `<Field>` + `<FieldLabel>` for forms. Set `invalid` when validation
 *   fails and pair with `<FieldError>` to display the message.
 * @see https://ui.shadcn.com/docs/components/textarea shadcn/ui Textarea documentation
 */
import type { LayoutClassName } from "@replohq/lattice/types/layoutClassName";
import type { VariantProps } from "class-variance-authority";

import * as React from "react";

import { cn } from "@replohq/lattice/utils/cn";
import { cva } from "class-variance-authority";

const textareaVariants = cva(
  "border border-component-border border-0.5 bg-sheet focus-visible:border-default focus-visible:shadow-2xs aria-invalid:border-error-border aria-invalid:bg-error-soft rounded-lg transition-all duration-300 placeholder:text-placeholder flex w-full outline-none disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      size: {
        sm: "px-2 py-1.5 text-xs",
        base: "px-3 py-2 text-sm",
      },
      variant: {
        short: "min-h-14",
        long: "min-h-20",
      },
    },
    defaultVariants: {
      size: "base",
      variant: "short",
    },
  },
);

interface TextareaProps<ClassName extends string = string>
  extends
    Omit<React.ComponentProps<"textarea">, "className">,
    VariantProps<typeof textareaVariants> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
  invalid?: boolean;
}

function TextareaInner<ClassName extends string>(
  {
    className,
    UNSAFE_className,
    size = "base",
    rows,
    variant = "short",
    invalid = false,
    ...props
  }: TextareaProps<ClassName>,
  ref: React.ForwardedRef<HTMLTextAreaElement>,
) {
  return (
    <textarea
      ref={ref}
      data-slot="textarea"
      rows={rows}
      className={cn(
        textareaVariants({ size, variant }),
        className,
        UNSAFE_className,
      )}
      aria-invalid={invalid}
      {...props}
    />
  );
}

// NOTE (Ben, 01/13/2026): Type cast to preserve generic type parameter for className prop
// See: https://fettblog.eu/typescript-react-generic-forward-refs/
// Legacy cast - may or may not be necessary
// eslint-disable-next-line replo/no-as-cast
const Textarea = React.forwardRef(TextareaInner) as <ClassName extends string>(
  props: TextareaProps<ClassName> & React.RefAttributes<HTMLTextAreaElement>,
) => React.ReactNode;

export { Textarea };
